import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getPropertyMeta,
  createPropertyMeta,
  patchPropertyMetaSocialStatus,
  patchPropertyMetaSocialLimit,
  deletePropertyMetaSocialLimit,
} from "../../api/properties/propertyMeta.api.js";

export const propertyMetaKey = (agentId, propertyId) => [
  "properties",
  agentId,
  propertyId,
  "meta",
];

const invalidatePropertyMeta = (queryClient, agentId, propertyId) => {
  queryClient.invalidateQueries({
    queryKey: ["properties"],
  });
  queryClient.invalidateQueries({
    queryKey: ["properties", agentId],
  });
  queryClient.invalidateQueries({
    queryKey: ["properties", agentId, propertyId],
  });
  queryClient.invalidateQueries({
    queryKey: propertyMetaKey(agentId, propertyId),
  });
};

const formatLoc = (loc) => {
  if (!Array.isArray(loc)) return null;
  const parts = loc.filter((part) => part !== "body" && typeof part === "string");
  return parts.length ? parts.join(".") : null;
};

const formatDetailArray = (detail) => {
  const messages = detail
    .map((item) => {
      if (typeof item === "string") return item;
      const msg = item?.msg;
      if (!msg) return null;
      const field = formatLoc(item.loc);
      return field ? `${field}: ${msg}` : msg;
    })
    .filter(Boolean);
  return messages.length ? messages.join("; ") : null;
};

const normalizeKnownString = (message) => {
  if (/not found/i.test(message)) return "Property not found";
  if (
    /cannot be empty/i.test(message) ||
    /empty body/i.test(message) ||
    /body required/i.test(message) ||
    /request body/i.test(message)
  ) {
    return "Request body cannot be empty";
  }
  return message;
};

export const getPropertyMetaErrorMessage = (error, fallback) => {
  if (typeof error === "string" && error) {
    return normalizeKnownString(error);
  }

  if (Array.isArray(error)) {
    return formatDetailArray(error) || fallback;
  }

  if (error?.detail != null && error.detail !== error) {
    return getPropertyMetaErrorMessage(error.detail, fallback);
  }

  if (typeof error?.message === "string" && error.message) {
    return normalizeKnownString(error.message);
  }

  return fallback;
};

export const usePropertyMetaQuery = (agentId, propertyId) => {
  return useQuery({
    queryKey: propertyMetaKey(agentId, propertyId),
    queryFn: () => getPropertyMeta(agentId, propertyId),
    enabled: !!agentId && !!propertyId,
  });
};

const usePropertyMetaMutation = (mutationFn) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (_, { agentId, propertyId }) => {
      invalidatePropertyMeta(queryClient, agentId, propertyId);
    },
  });
};

export const useCreatePropertyMetaMutation = () =>
  usePropertyMetaMutation(({ agentId, propertyId, data }) =>
    createPropertyMeta(agentId, propertyId, data),
  );

export const usePatchPropertyMetaSocialStatusMutation = () =>
  usePropertyMetaMutation(({ agentId, propertyId, data }) =>
    patchPropertyMetaSocialStatus(agentId, propertyId, data),
  );

export const usePatchPropertyMetaSocialLimitMutation = () =>
  usePropertyMetaMutation(({ agentId, propertyId, data }) =>
    patchPropertyMetaSocialLimit(agentId, propertyId, data),
  );

export const useDeletePropertyMetaSocialLimitMutation = () =>
  usePropertyMetaMutation(({ agentId, propertyId }) =>
    deletePropertyMetaSocialLimit(agentId, propertyId),
  );
